// ─── Cosmetic skin catalogue (web) ─────────────────────────────────────────
// Web equivalent of src/game/audioSkins.ts. Can't reuse that file directly —
// it loads tracks via React Native's `require()`; the web build serves the
// same audio files as plain URLs from /public/audio (see scripts/copy-assets.mjs).
// bpm + accentColor values MUST match src/game/audioSkins.ts exactly — bpm
// drives real beat-sync gameplay math (see engine/game.ts).
//
// entitlementId mirrors the RevenueCat entitlement identifiers in
// src/api/useIAP.ts so the two platforms share one vocabulary even though the
// purchase/storage path differs (Stripe + web_entitlements here, RevenueCat +
// store receipts there). docs/spec.md §4 "2026-09-17 — Web IAP override".

export interface WebSkin {
  id: string;
  name: string;
  url: string;
  bpm: number;
  accentColor: string;
  free: boolean;
  entitlementId: string | null; // null only for the free skin
  priceLabel: string;
  bundleEligible: boolean; // included in the skin_bundle_all grant
}

export const WEB_SKINS: WebSkin[] = [
  {
    id: 'puzzle-rush',
    name: 'Puzzle Rush',
    url: '/audio/puzzle-rush.mp3',
    bpm: 120,
    accentColor: '#6C63FF',
    free: true,
    entitlementId: null,
    priceLabel: 'Free',
    bundleEligible: false,
  },
  {
    id: 'synthwave',
    name: 'Synthwave',
    url: '/audio/synthwave.mp3',
    bpm: 128,
    accentColor: '#00F0FF',
    free: false,
    entitlementId: 'skin_synthwave',
    priceLabel: '$0.99',
    bundleEligible: true,
  },
  {
    id: 'beach-vibes',
    name: 'Beach Vibes',
    url: '/audio/beach-vibes.mp3',
    bpm: 95,
    accentColor: '#FF6B6B',
    free: false,
    entitlementId: 'skin_beach',
    priceLabel: '$0.99',
    bundleEligible: true,
  },
  {
    id: 'nz-roots',
    name: 'NZ Roots',
    url: '/audio/nz-roots.mp3',
    bpm: 90,
    accentColor: '#2ECC71',
    free: false,
    entitlementId: 'skin_nz_roots',
    priceLabel: '$0.99',
    bundleEligible: true,
  },
  {
    id: 'chaos-storm',
    name: 'Storm (Chaos Pack)',
    url: '/audio/chaos-storm.mp3',
    bpm: 132,
    accentColor: '#00E5FF',
    free: false,
    entitlementId: 'skin_chaos_storm',
    priceLabel: '$0.99',
    bundleEligible: true,
  },
  {
    id: 'chaos-disco',
    name: 'Disco (Chaos Pack)',
    url: '/audio/chaos-disco.mp3',
    bpm: 118,
    accentColor: '#FF44D6',
    free: false,
    entitlementId: 'skin_chaos_disco',
    priceLabel: '$0.99',
    bundleEligible: true,
  },
  {
    id: 'halloween',
    name: 'Haunted Cascade',
    url: '/audio/halloween.mp3',
    bpm: 128,
    accentColor: '#FF7518',
    free: false,
    entitlementId: 'skin_halloween',
    priceLabel: '$0.99',
    bundleEligible: false, // seasonal — not part of the fixed 5-skin bundle (matches native §4.3a)
  },
];

export const BUNDLE_ENTITLEMENT_ID = 'skin_bundle_all';
export const BUNDLE_PRICE_LABEL = '$2.99';
export const BUNDLE_SKIN_IDS = WEB_SKINS.filter((s) => s.bundleEligible).map((s) => s.id);

export function findWebSkin(id: string): WebSkin {
  return WEB_SKINS.find((s) => s.id === id) ?? WEB_SKINS[0];
}
